"use client";
/* eslint-disable @next/next/no-img-element -- imágenes del bucket público booking-media */

import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import type { CSSProperties } from "react";
import { ArrowLeft, CalendarPlus, Check, ChevronLeft, ChevronRight, Loader2, MessageCircle } from "lucide-react";

import type { ResolvedTheme } from "@/core/booking/themes";
import { formatMinutes } from "@/core/domain/service-pose";
import { formatArs } from "@/core/domain/service-pricing";
import {
  getCartDaysAction,
  getCartSlotsAction,
  submitCartBookingAction,
  type CartBookingState,
} from "@/app/p/[slug]/reservar/actions";

// =====================================================================
// Flujo de reserva v2 (E2 · F5). Diseño: canvas E2, Flujo.dc.html.
// El cliente sólo arma la selección; duración, precio, disponibilidad y la
// agenda en la que cae se recalculan en el servidor al confirmar.
// =====================================================================

export type FlowService = {
  id: string;
  slug: string | null;
  name: string;
  categoryId: string | null;
  description: string | null;
  photoUrl: string | null;
  durationMin: number;
  price: number | null;
  priceText: string | null;
  showPrices: boolean;
  poseText: string | null;
  variants: Array<{ id: string; name: string; durationMin: number; price: number | null }>;
  packs: Array<{ id: string; name: string; sessions: number; price: number; validDays: number | null }>;
};

export type FlowData = {
  slug: string;
  name: string;
  logoUrl: string | null;
  theme: ResolvedTheme;
  categories: Array<{ id: string; name: string }>;
  services: FlowService[];
  people: Array<{
    agendaId: string;
    slug: string | null;
    name: string;
    role: string | null;
    photoUrl: string | null;
    /** Vacío = hace todos los servicios. */
    serviceIds: string[];
  }>;
  flowStart: "servicio" | "profesional";
  anyProfessional: boolean;
  autoConfirm: boolean;
  policies: Array<{ title: string; text: string }>;
  whatsappUrl: string | null;
  address: string | null;
  timeZone: string;
  today: string;
  maxDaysAhead: number;
  initialServiceId: string | null;
  initialAgendaId: string | null;
  returning: {
    token: string;
    fullName: string;
    phone: string | null;
    email: string | null;
    lastServiceIds: string[];
    balance: Array<{ serviceId: string; serviceName: string; available: number; nextExpiry: string | null }>;
  } | null;
};

type Line = { serviceId: string; variantId: string | null; packId: string | null; useCredit?: boolean };
type Step = "servicio" | "profesional" | "horario" | "datos" | "confirmacion";
const ANY = "any";

function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

function monthOf(ymd: string): string {
  return `${ymd.slice(0, 7)}-01`;
}

const WEEKDAY = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

function dayParts(ymd: string) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return { dow: WEEKDAY[dt.getUTCDay()], num: d };
}

function longDate(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("es-AR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

function canDo(person: FlowData["people"][number], serviceIds: string[]): boolean {
  return person.serviceIds.length === 0 || serviceIds.every((id) => person.serviceIds.includes(id));
}

function icsFor(data: FlowData, date: string, start: string, end: string, title: string): string {
  const dt = (t: string) => `${date.replace(/-/g, "")}T${t.replace(":", "")}00`;
  const esc = (v: string) => v.replace(/[\\,;]/g, (c) => `\\${c}`).replace(/\n/g, "\\n");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Sesionia//Reserva//ES",
    "BEGIN:VEVENT",
    `UID:${date}-${start}-${data.slug}@sesionia.com`,
    `DTSTART;TZID=${data.timeZone}:${dt(start)}`,
    `DTEND;TZID=${data.timeZone}:${dt(end)}`,
    `SUMMARY:${esc(`${title} — ${data.name}`)}`,
    ...(data.address ? [`LOCATION:${esc(data.address)}`] : []),
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

export function BookingFlow({ data }: { data: FlowData }) {
  const t = data.theme;
  const byId = useMemo(() => new Map(data.services.map((s) => [s.id, s])), [data.services]);

  const firstVariant = (serviceId: string) => byId.get(serviceId)?.variants[0]?.id ?? null;
  // Sesiones de saldo de la persona que vuelve: por defecto se usan.
  const creditsFor = (serviceId: string) =>
    data.returning?.balance.find((b) => b.serviceId === serviceId)?.available ?? 0;
  const initialLines: Line[] = data.initialServiceId
    ? [
        {
          serviceId: data.initialServiceId,
          variantId: firstVariant(data.initialServiceId),
          packId: null,
          useCredit: creditsFor(data.initialServiceId) > 0,
        },
      ]
    : [];

  const [lines, setLines] = useState<Line[]>(initialLines);
  const [agenda, setAgenda] = useState<string | null>(data.initialAgendaId);
  const [category, setCategory] = useState<string | null>(data.categories[0]?.id ?? null);
  const [date, setDate] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [weekStart, setWeekStart] = useState(data.today);
  const [days, setDays] = useState<Set<string>>(new Set());
  const [loadedMonths, setLoadedMonths] = useState<Set<string>>(new Set());
  const [slots, setSlots] = useState<string[] | null>(null);
  const [name, setName] = useState(data.returning?.fullName ?? "");
  const [phone, setPhone] = useState(data.returning?.phone ?? "");
  const [email, setEmail] = useState(data.returning?.email ?? "");
  const [accept, setAccept] = useState(false);
  const [website, setWebsite] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CartBookingState | null>(null);
  const [pending, startTransition] = useTransition();
  const [loading, setLoading] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const serviceIds = lines.map((l) => l.serviceId);
  const peopleForCart = data.people.filter((p) => canDo(p, serviceIds));
  const askPerson = data.people.length > 1;

  const allSteps: Step[] = useMemo(() => {
    const first: Step[] =
      data.flowStart === "profesional" && askPerson ? ["profesional", "servicio"] : ["servicio"];
    const second: Step[] = data.flowStart === "profesional" || !askPerson ? [] : ["profesional"];
    return [...first, ...second, "horario", "datos"];
  }, [data.flowStart, askPerson]);
  // Si una sola persona puede hacer todo, "¿Con quién?" no se muestra: no
  // cuenta en "Paso X de N" ni aparece al volver atrás.
  const skipPerson = data.flowStart !== "profesional" && askPerson && peopleForCart.length === 1;
  const steps: Step[] = useMemo(
    () => (skipPerson ? allSteps.filter((s) => s !== "profesional") : allSteps),
    [allSteps, skipPerson],
  );
  const [step, setStep] = useState<Step>(allSteps[0]);
  const stepIdx = steps.indexOf(step);

  // Duración y total del carrito (el servidor recalcula al confirmar).
  const resolved = lines.map((l) => {
    const s = byId.get(l.serviceId)!;
    const v = s.variants.find((x) => x.id === l.variantId) ?? null;
    const k = s.packs.find((x) => x.id === l.packId) ?? null;
    return {
      svc: s,
      variant: v,
      pack: k,
      duration: v ? v.durationMin : s.durationMin,
      // Con saldo no se paga nada en el local por este servicio.
      price: l.useCredit ? 0 : k ? k.price : v ? v.price : s.price,
      label: v ? `${s.name} · ${v.name}` : s.name,
    };
  });
  const totalMin = resolved.reduce((a, r) => a + r.duration, 0);
  const priced = resolved.every((r) => r.price !== null && (r.svc.showPrices || r.price === 0));
  const total = resolved.reduce((a, r) => a + (r.price ?? 0), 0);
  const agendaParam = agenda && agenda !== ANY ? agenda : null;
  const cartKey = JSON.stringify([lines, agendaParam]);

  // Cambia la selección → se reinicia el horario.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDate(null);
      setTime(null);
      setSlots(null);
      setDays(new Set());
      setLoadedMonths(new Set());
      setWeekStart(data.today);
    }, 0);
    return () => window.clearTimeout(id);
  }, [cartKey, data.today]);

  const lastDay = addDays(data.today, data.maxDaysAhead);
  const week = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).filter((d) => d <= lastDay),
    [weekStart, lastDay],
  );

  // Días con lugar de los meses visibles (en lotes, por mes).
  useEffect(() => {
    if (step !== "horario" || lines.length === 0) return;
    const months = [...new Set(week.map(monthOf))].filter((m) => !loadedMonths.has(m));
    if (months.length === 0) return;
    let alive = true;
    const run = async () => {
      setLoading(true);
      try {
        const res = await Promise.all(
          months.map((m) => getCartDaysAction(data.slug, lines, m, agendaParam)),
        );
        if (!alive) return;
        setDays((prev) => new Set([...prev, ...res.flat()]));
        setLoadedMonths((prev) => new Set([...prev, ...months]));
      } catch {
        if (alive) setError("No pudimos cargar los días. Probá de nuevo.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    void run();
    return () => {
      alive = false;
    };
  }, [step, week, loadedMonths, lines, agendaParam, data.slug]);

  async function pickDate(d: string) {
    setDate(d);
    setTime(null);
    setSlots(null);
    try {
      setSlots(await getCartSlotsAction(data.slug, lines, d, agendaParam));
    } catch {
      setSlots([]);
      setError("No pudimos cargar los horarios. Probá de nuevo.");
    }
  }

  function toggleService(id: string) {
    setError(null);
    setLines((prev) =>
      prev.some((l) => l.serviceId === id)
        ? prev.filter((l) => l.serviceId !== id)
        : prev.length >= 5
          ? prev
          : [...prev, { serviceId: id, variantId: firstVariant(id), packId: null, useCredit: creditsFor(id) > 0 }],
    );
  }

  function updateLine(id: string, patch: Partial<Line>) {
    setLines((prev) => prev.map((l) => (l.serviceId === id ? { ...l, ...patch } : l)));
  }

  const canNext =
    step === "servicio"
      ? lines.length > 0 && (!agendaParam || peopleForCart.some((p) => p.agendaId === agendaParam))
      : step === "profesional"
        ? agenda !== null
        : step === "horario"
          ? !!date && !!time
          : step === "datos"
            ? name.trim().length >= 2 && phone.replace(/\D/g, "").length >= 6 && accept
            : false;

  // Cada paso es una entrada del historial: el "atrás" del navegador o del
  // celular vuelve al paso anterior en vez de sacar a la persona de la reserva.
  const stepsRef = useRef(steps);
  const resultRef = useRef(result);
  useEffect(() => {
    stepsRef.current = steps;
    resultRef.current = result;
  }, [steps, result]);
  useEffect(() => {
    function onPop(e: PopStateEvent) {
      if (resultRef.current) return; // ya reservó: no reabrir el formulario
      const target = (e.state as { bpStep?: Step } | null)?.bpStep;
      setError(null);
      setStep(target && stepsRef.current.includes(target) ? target : stepsRef.current[0]);
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  function go(next: Step) {
    setError(null);
    setStep(next);
    if (next !== "confirmacion") {
      try {
        window.history.pushState({ bpStep: next }, "");
      } catch {
        /* sin historial: el paso igual cambia */
      }
    }
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function back() {
    if (stepIdx <= 0) return;
    const st = window.history.state as { bpStep?: Step } | null;
    if (st?.bpStep === step) window.history.back();
    else go(steps[stepIdx - 1]);
  }

  // Qué falta para seguir (el botón apagado se explica solo).
  const missingHint = canNext || step === "confirmacion" ? null : missingFor(step, {
    hasServices: lines.length > 0,
    hasSlot: !!date && !!time,
    name: name.trim().length >= 2,
    phone: phone.replace(/\D/g, "").length >= 6,
    accept,
  });

  function next() {
    if (!canNext) return;
    if (step === "datos") {
      startTransition(async () => {
        try {
          const res = await submitCartBookingAction(data.slug, {
            lines,
            agendaId: agendaParam,
            date,
            startTime: time,
            contactName: name,
            contactPhone: phone,
            contactEmail: email,
            note: "",
            website,
            acceptPolicies: accept,
            token: data.returning?.token,
          });
          if (res.status === "error") {
            setError(res.message);
            if (res.message.includes("horario")) {
              setTime(null);
              if (date) void pickDate(date);
              go("horario");
            }
            return;
          }
          setResult(res);
          go("confirmacion");
        } catch {
          setError("No pudimos reservar por un problema nuestro. Probá de nuevo en un rato.");
        }
      });
      return;
    }
    // Saltear "¿Con quién?" si una sola persona puede hacer todo.
    if (skipPerson && step === "servicio") setAgenda(peopleForCart[0].agendaId);
    go(steps[stepIdx + 1]);
  }

  // ---------- estilos del tema ----------
  const vars = {
    "--bp-bg": t.bg,
    "--bp-surface": t.surface,
    "--bp-ink": t.ink,
    "--bp-muted": t.muted,
    "--bp-line": t.line,
    "--bp-accent": t.accent,
    "--bp-on-accent": t.onAccent,
    "--bp-tint": t.accentTint,
    "--bp-accent-text": t.accentText,
    "--bp-r": `${t.radius}px`,
    "--bp-r-chip": `${t.radiusChip}px`,
    "--bp-r-btn": `${t.radiusButton}px`,
    fontFamily: t.bodyFont,
  } as CSSProperties;
  const h1: CSSProperties = {
    fontFamily: t.headFont,
    fontWeight: t.headWeight,
    textTransform: t.headCase,
    letterSpacing: t.headTracking,
    fontSize: t.editorial ? 32 : 26,
    lineHeight: 1.15,
  };
  const btnText: CSSProperties = t.editorial
    ? { textTransform: "uppercase", letterSpacing: "0.12em", fontSize: 13 }
    : {};
  const chip = (on: boolean) =>
    on
      ? "rounded-[var(--bp-r-chip)] bg-[var(--bp-accent)] px-3.5 py-2 text-[13px] font-semibold text-[var(--bp-on-accent)]"
      : "rounded-[var(--bp-r-chip)] border border-[var(--bp-line)] px-3.5 py-2 text-[13px]";
  const option = (on: boolean) =>
    on
      ? "rounded-[var(--bp-r-chip)] border border-[var(--bp-ink)] px-3 py-1.5 text-xs font-semibold"
      : "rounded-[var(--bp-r-chip)] border border-[var(--bp-line)] px-3 py-1.5 text-xs text-[var(--bp-muted)]";
  const card = (on: boolean) =>
    `flex flex-col gap-3 rounded-[var(--bp-r)] border p-3.5 ${on ? "border-[var(--bp-accent)] bg-[var(--bp-surface)]" : "border-[var(--bp-line)] bg-[var(--bp-surface)]"}`;
  const avatarRadius = t.editorial ? "2px" : "999px";

  const visibleServices = data.services.filter(
    (s) =>
      (data.categories.length <= 1 || !category || s.categoryId === category || !s.categoryId) &&
      (!agendaParam ||
        data.people.find((p) => p.agendaId === agendaParam)?.serviceIds.length === 0 ||
        data.people.find((p) => p.agendaId === agendaParam)?.serviceIds.includes(s.id)),
  );

  const slotsByPart = (slots ?? []).reduce<Record<string, string[]>>((acc, s) => {
    const h = Number(s.slice(0, 2));
    const k = h < 12 ? "Mañana" : h < 19 ? "Tarde" : "Noche";
    (acc[k] ??= []).push(s);
    return acc;
  }, {});
  const nextBookable = [...days].sort().find((d) => d > (date ?? data.today));

  const ctaText =
    step === "datos" ? (data.autoConfirm ? "Confirmar turno" : "Pedir turno") : "Continuar";

  // ---------- render ----------
  return (
    <div style={vars} className="flex min-h-dvh flex-col bg-[var(--bp-bg)] text-[15px] text-[var(--bp-ink)]">
      <div ref={topRef} className="mx-auto flex w-full max-w-xl flex-1 flex-col">
        {/* Encabezado */}
        <div className="flex flex-col gap-3 px-5 pt-5">
          <div className="flex items-center gap-3">
            {stepIdx > 0 && step !== "confirmacion" ? (
              <button type="button" onClick={back} aria-label="Volver al paso anterior" className="-ml-1 p-1">
                <ArrowLeft className="size-5" />
              </button>
            ) : (
              <a href={`/p/${data.slug}`} aria-label="Volver a la página del negocio" className="-ml-1 p-1">
                <ArrowLeft className="size-5" />
              </a>
            )}
            {data.logoUrl ? (
              <img src={data.logoUrl} alt={data.name} className="size-8 object-cover" style={{ borderRadius: avatarRadius }} />
            ) : null}
            <span className="font-semibold">{data.name}</span>
            {step !== "confirmacion" && (
              <span className="ml-auto text-xs text-[var(--bp-muted)]">
                Paso {stepIdx + 1} de {steps.length}
              </span>
            )}
          </div>
          {step !== "confirmacion" && (
            <div className="flex gap-1.5" aria-hidden>
              {steps.map((s, i) => (
                <span
                  key={s}
                  className="h-1 flex-1 rounded-full"
                  style={{ background: i <= stepIdx ? t.accent : t.line }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-4 px-5 pt-6 pb-36">
          {error && (
            <div role="alert" className="rounded-[var(--bp-r)] border border-[var(--bp-line)] bg-[var(--bp-surface)] p-3 text-sm">
              {error}
            </div>
          )}

          {stepIdx === 0 && data.returning && (
            <div className="flex flex-col gap-1.5 rounded-[var(--bp-r)] bg-[var(--bp-tint)] p-3.5 text-sm">
              <span className="font-semibold">Hola, {data.returning.fullName.split(" ")[0]}</span>
              {data.returning.balance.map((b) => (
                <span key={b.serviceName}>
                  Te {b.available === 1 ? "queda 1 sesión" : `quedan ${b.available} sesiones`} de {b.serviceName}
                  {b.nextExpiry ? ` (vencen el ${new Date(b.nextExpiry).toLocaleDateString("es-AR", { day: "numeric", month: "long" })})` : ""}.
                </span>
              ))}
              {data.returning.lastServiceIds.length > 0 && lines.length === 0 && (
                <button
                  type="button"
                  className="w-fit text-sm font-semibold underline underline-offset-[3px]"
                  onClick={() =>
                    setLines(
                      data.returning!.lastServiceIds.map((id) => ({
                        serviceId: id,
                        variantId: firstVariant(id),
                        packId: null,
                        useCredit: creditsFor(id) > 0,
                      })),
                    )
                  }
                >
                  Repetir mi último turno
                </button>
              )}
            </div>
          )}
          {/* ---------- Servicios ---------- */}
          {step === "servicio" && (
            <>
              <h1 className="m-0" style={h1}>
                ¿Qué te querés hacer?
              </h1>
              {agendaParam && (
                <p className="m-0 text-sm text-[var(--bp-muted)]">
                  Con {data.people.find((p) => p.agendaId === agendaParam)?.name}
                </p>
              )}
              {data.categories.length > 1 && (
                <div className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1">
                  {data.categories.map((c) => (
                    <button key={c.id} type="button" aria-pressed={category === c.id} onClick={() => setCategory(c.id)} className={`shrink-0 whitespace-nowrap ${chip(category === c.id)}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex flex-col gap-3">
                {visibleServices.map((s) => {
                  const line = lines.find((l) => l.serviceId === s.id);
                  const sel = !!line;
                  const pack = s.packs.find((k) => k.id === line?.packId) ?? null;
                  const minEach = s.packs.length ? Math.min(...s.packs.map((k) => k.price / k.sessions)) : null;
                  return (
                    <div key={s.id} className={card(sel)}>
                      <div className="flex gap-3">
                        {s.photoUrl && <img src={s.photoUrl} alt="" className="size-16 shrink-0 rounded-[var(--bp-r-chip)] object-cover" style={{ borderRadius: Math.max(2, t.radius - 6) }} />}
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <span className="font-semibold">{s.name}</span>
                          {s.description && <span className="text-sm text-[var(--bp-muted)]">{s.description}</span>}
                          <span className="text-[13px] text-[var(--bp-muted)]">
                            {formatMinutes(s.durationMin)}
                            {s.priceText ? ` · ${s.priceText}` : ""}
                            {minEach && s.showPrices ? ` · packs desde ${formatArs(minEach)} c/u` : ""}
                          </span>
                        </div>
                        <button
                          type="button"
                          aria-pressed={sel}
                          onClick={() => toggleService(s.id)}
                          className={`h-9 shrink-0 self-start rounded-[var(--bp-r-btn)] px-3.5 text-sm font-semibold ${sel ? "bg-[var(--bp-accent)] text-[var(--bp-on-accent)]" : "border border-[var(--bp-line)]"}`}
                        >
                          {sel ? "Agregado" : "Agregar"}
                        </button>
                      </div>
                      {sel && s.variants.length > 0 && (
                        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={`Opciones de ${s.name}`}>
                          {s.variants.map((v) => (
                            <button key={v.id} type="button" role="radio" aria-checked={line!.variantId === v.id} onClick={() => updateLine(s.id, { variantId: v.id })} className={option(line!.variantId === v.id)}>
                              {v.name}
                              {s.showPrices && v.price !== null ? ` · ${formatArs(v.price)}` : ""}
                            </button>
                          ))}
                        </div>
                      )}
                      {sel && (s.packs.length > 0 || creditsFor(s.id) > 0) && (
                        <div className="flex flex-col gap-2">
                          <span className="text-xs font-semibold text-[var(--bp-muted)]">Cómo lo pagás</span>
                          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Cómo lo pagás">
                            {creditsFor(s.id) > 0 && (
                              <button type="button" role="radio" aria-checked={!!line!.useCredit} onClick={() => updateLine(s.id, { packId: null, useCredit: true })} className={option(!!line!.useCredit)}>
                                Usar 1 de tus {creditsFor(s.id)} {creditsFor(s.id) === 1 ? "sesión" : "sesiones"}
                              </button>
                            )}
                            <button type="button" role="radio" aria-checked={!line!.packId && !line!.useCredit} onClick={() => updateLine(s.id, { packId: null, useCredit: false })} className={option(!line!.packId && !line!.useCredit)}>
                              Sesión suelta{s.showPrices && s.price !== null ? ` · ${formatArs(s.price)}` : ""}
                            </button>
                            {s.packs.map((k) => (
                              <button key={k.id} type="button" role="radio" aria-checked={line!.packId === k.id} onClick={() => updateLine(s.id, { packId: k.id, useCredit: false })} className={option(line!.packId === k.id)}>
                                {k.name} · {formatArs(k.price)} ({formatArs(k.price / k.sessions)} c/u)
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {sel && ((s.poseText && s.variants.length === 0) || pack || line!.useCredit) && (
                        <div className="text-xs font-semibold text-[var(--bp-accent-text)]">
                          {[
                            // La pose está definida para la duración base (sin opciones).
                            s.variants.length === 0 ? s.poseText : null,
                            pack
                              ? `${pack.name}: se paga en el local en tu primer turno y te quedan ${pack.sessions - 1} sesiones para reservar cuando quieras${pack.validDays ? ` (vencen a los ${pack.validDays} días)` : ""}`
                              : null,
                            line!.useCredit ? "Se descuenta 1 sesión de tu saldo: no pagás nada en el local por este servicio" : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      )}
                    </div>
                  );
                })}
                {visibleServices.length === 0 && (
                  <p className="text-sm text-[var(--bp-muted)]">No hay servicios para reservar online en esta categoría.</p>
                )}
              </div>
            </>
          )}

          {/* ---------- Con quién ---------- */}
          {step === "profesional" && (
            <>
              <h1 className="m-0" style={h1}>
                ¿Con quién?
              </h1>
              <p className="m-0 text-sm text-[var(--bp-muted)]">
                {lines.length > 0 ? "Te mostramos quiénes hacen lo que elegiste." : "Elegí con quién te querés atender."}
              </p>
              <div className="flex flex-col gap-2">
                {data.anyProfessional && (lines.length === 0 || peopleForCart.length > 1) && (
                  <PersonOption name="Me da igual" role="Te damos el primer turno libre" initial="?" photoUrl={null} on={agenda === ANY} onPick={() => setAgenda(ANY)} radius={avatarRadius} dashed />
                )}
                {(lines.length > 0 ? peopleForCart : data.people).map((p) => (
                  <PersonOption key={p.agendaId} name={p.name} role={p.role} initial={p.name.charAt(0).toUpperCase()} photoUrl={p.photoUrl} on={agenda === p.agendaId} onPick={() => setAgenda(p.agendaId)} radius={avatarRadius} />
                ))}
              </div>
            </>
          )}

          {/* ---------- Día y horario ---------- */}
          {step === "horario" && (
            <>
              <div className="flex flex-col gap-1">
                <h1 className="m-0" style={h1}>
                  Elegí día y horario
                </h1>
                <p className="m-0 text-sm text-[var(--bp-muted)]">
                  {agendaParam ? `Con ${data.people.find((p) => p.agendaId === agendaParam)?.name}` : askPerson ? "Con quien tenga el primer turno" : data.name} · {formatMinutes(totalMin)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" aria-label="Semana anterior" disabled={weekStart <= data.today} onClick={() => setWeekStart(addDays(weekStart, -7) < data.today ? data.today : addDays(weekStart, -7))} className="p-1 disabled:opacity-30">
                  <ChevronLeft className="size-5" />
                </button>
                <div className="grid flex-1 grid-cols-7 gap-1.5">
                  {week.map((d) => {
                    const { dow, num } = dayParts(d);
                    const open = days.has(d);
                    const on = date === d;
                    return (
                      <button
                        key={d}
                        type="button"
                        aria-pressed={on}
                        aria-label={longDate(d)}
                        disabled={!open}
                        onClick={() => pickDate(d)}
                        className={`flex flex-col items-center gap-0.5 rounded-[var(--bp-r-chip)] border py-2 text-xs disabled:opacity-35 ${on ? "border-[var(--bp-accent)] bg-[var(--bp-accent)] text-[var(--bp-on-accent)]" : "border-[var(--bp-line)]"}`}
                        style={{ borderRadius: Math.min(t.radius, 12) }}
                      >
                        <span>{d === data.today ? "Hoy" : dow}</span>
                        <span className="text-base font-semibold">{num}</span>
                      </button>
                    );
                  })}
                </div>
                <button type="button" aria-label="Semana siguiente" disabled={addDays(weekStart, 7) > lastDay} onClick={() => setWeekStart(addDays(weekStart, 7))} className="p-1 disabled:opacity-30">
                  <ChevronRight className="size-5" />
                </button>
              </div>
              {loading && (
                <span className="inline-flex items-center gap-2 text-sm text-[var(--bp-muted)]">
                  <Loader2 className="size-4 animate-spin" /> Buscando lugares…
                </span>
              )}
              {!loading && week.every((d) => !days.has(d)) && (
                <div className="flex flex-col gap-2 rounded-[var(--bp-r)] border border-[var(--bp-line)] p-3.5 text-sm">
                  <span>No quedan turnos esta semana.</span>
                  {nextBookable ? (
                    <button type="button" onClick={() => { setWeekStart(nextBookable); void pickDate(nextBookable); }} className="w-fit font-semibold underline underline-offset-[3px]">
                      Ver el {longDate(nextBookable)}
                    </button>
                  ) : (
                    <button type="button" onClick={() => setWeekStart(addDays(weekStart, 7))} disabled={addDays(weekStart, 7) > lastDay} className="w-fit font-semibold underline underline-offset-[3px] disabled:opacity-40">
                      Ver la semana siguiente
                    </button>
                  )}
                </div>
              )}
              {date && (
                <div className="flex flex-col gap-3">
                  <p className="m-0 text-sm font-semibold first-letter:uppercase">{longDate(date)}</p>
                  {slots === null ? (
                    <span className="inline-flex items-center gap-2 text-sm text-[var(--bp-muted)]">
                      <Loader2 className="size-4 animate-spin" /> Cargando horarios…
                    </span>
                  ) : slots.length === 0 ? (
                    <span className="text-sm text-[var(--bp-muted)]">No quedan horarios este día. Probá con otro.</span>
                  ) : (
                    (["Mañana", "Tarde", "Noche"] as const)
                      .filter((k) => slotsByPart[k]?.length)
                      .map((k) => (
                        <div key={k} className="flex flex-col gap-2">
                          <span className="text-xs font-semibold text-[var(--bp-muted)]">{k}</span>
                          <div className="flex flex-wrap gap-2">
                            {slotsByPart[k].map((h) => (
                              <button key={h} type="button" aria-pressed={time === h} onClick={() => setTime(h)} className={chip(time === h)}>
                                {h}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              )}
            </>
          )}

          {/* ---------- Datos ---------- */}
          {step === "datos" && (
            <>
              <div className="flex flex-col gap-1">
                <h1 className="m-0" style={h1}>
                  Tus datos
                </h1>
                <p className="m-0 text-sm text-[var(--bp-muted)]">No hace falta crear una cuenta.</p>
              </div>
              <Field label="Nombre y apellido" id="f-nombre">
                <input id="f-nombre" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className="h-11 w-full rounded-[var(--bp-r-chip)] border border-[var(--bp-line)] bg-[var(--bp-surface)] px-3" style={{ borderRadius: Math.min(t.radius, 12) }} />
              </Field>
              <Field label="Celular" id="f-cel" help="Por acá te avisamos si hay algún cambio.">
                <input id="f-cel" type="tel" inputMode="tel" autoComplete="tel" placeholder="280 412-3456" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={30} className="h-11 w-full rounded-[var(--bp-r-chip)] border border-[var(--bp-line)] bg-[var(--bp-surface)] px-3" style={{ borderRadius: Math.min(t.radius, 12) }} />
              </Field>
              <Field label="Email (opcional)" id="f-mail" help="Para mandarte la confirmación y tu link para reservar de nuevo.">
                <input id="f-mail" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} className="h-11 w-full rounded-[var(--bp-r-chip)] border border-[var(--bp-line)] bg-[var(--bp-surface)] px-3" style={{ borderRadius: Math.min(t.radius, 12) }} />
              </Field>
              {/* Honeypot anti-bots: invisible para personas. */}
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden className="hidden" value={website} onChange={(e) => setWebsite(e.target.value)} />
              <div className="flex flex-col gap-2 rounded-[var(--bp-r)] border border-[var(--bp-line)] bg-[var(--bp-surface)] p-3.5">
                <span className="text-sm font-semibold">Antes de confirmar</span>
                {data.policies.map((p) => (
                  <div key={p.title} className="flex flex-col text-sm">
                    <span className="font-semibold">{p.title}</span>
                    <span className="text-[var(--bp-muted)]">{p.text}</span>
                  </div>
                ))}
                <div className="flex items-start gap-2.5 pt-1 text-sm">
                  <input id="f-acepta" type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-0.5 size-4" style={{ accentColor: t.accent }} />
                  <label htmlFor="f-acepta">Leí y acepto estas condiciones</label>
                </div>
              </div>
            </>
          )}

          {/* ---------- Confirmación ---------- */}
          {step === "confirmacion" && result && result.status !== "idle" && result.status !== "error" && (
            <div className="flex flex-col items-center gap-4 pt-4 text-center">
              <span className="flex size-14 items-center justify-center rounded-full bg-[var(--bp-tint)]">
                <Check className="size-7" />
              </span>
              <div className="flex flex-col gap-1">
                <h1 className="m-0" style={h1}>
                  {result.status === "confirmed" ? "¡Listo! Tu turno quedó confirmado" : "Recibimos tu pedido"}
                </h1>
                <p className="m-0 text-sm text-[var(--bp-muted)]">
                  {result.status === "confirmed"
                    ? result.emailSent ? "Te mandamos la confirmación por email." : "Te esperamos."
                    : "Te avisamos apenas lo confirmen."}
                </p>
              </div>
              <div className="flex w-full flex-col gap-2 rounded-[var(--bp-r)] border border-[var(--bp-line)] bg-[var(--bp-surface)] p-4 text-left text-sm">
                <span className="font-semibold first-letter:uppercase">
                  {longDate(result.date)} · {result.startTime} a {result.endTime}
                </span>
                {result.lines.map((l) => (
                  <span key={l.label}>
                    {l.label}
                    {l.pack ? ` — ${l.pack} (se paga en el local en tu primer turno)` : ""}
                    {l.credit ? " — con 1 sesión de tu saldo" : ""}
                  </span>
                ))}
                {data.address && <span className="text-[var(--bp-muted)]">{data.address}</span>}
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                <a
                  download="turno.ics"
                  href={`data:text/calendar;charset=utf-8,${encodeURIComponent(icsFor(data, result.date, result.startTime, result.endTime, result.lines.map((l) => l.label).join(" + ")))}`}
                  className="inline-flex h-10 items-center gap-2 rounded-[var(--bp-r-btn)] border border-[var(--bp-line)] px-4 text-sm font-semibold"
                >
                  <CalendarPlus className="size-4" /> Agregar al calendario
                </a>
                {data.whatsappUrl && (
                  <a href={data.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-10 items-center gap-2 rounded-[var(--bp-r-btn)] border border-[var(--bp-line)] px-4 text-sm font-semibold">
                    <MessageCircle className="size-4" /> WhatsApp al local
                  </a>
                )}
              </div>
              {result.emailSent && (
                <p className="m-0 text-xs text-[var(--bp-muted)]">
                  En el email tenés un link para cancelar y otro para reservar de nuevo cuando quieras.
                </p>
              )}
              <a href={`/p/${data.slug}`} className="text-sm underline underline-offset-[3px]">
                Volver a la página
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Barra inferior */}
      {step !== "confirmacion" && (
        <div className="sticky bottom-0 z-10 border-t border-[var(--bp-line)] bg-[var(--bp-surface)]">
          <div className="mx-auto flex max-w-xl items-center justify-between gap-3 px-5 pt-3 pb-5">
            <div className="flex min-w-0 flex-col">
              <span className="line-clamp-2 text-sm font-bold">
                {lines.length === 0
                  ? "Elegí un servicio"
                  : `${lines.length === 1 ? resolved[0].label : `${lines.length} servicios`} · ${formatMinutes(totalMin)}`}
              </span>
              {missingHint && (
                <span role="status" className="text-xs text-[var(--bp-muted)]">
                  {missingHint}
                </span>
              )}
              {lines.length > 0 && !missingHint && (
                <span className="truncate text-xs text-[var(--bp-muted)]">
                  {lines.every((l) => l.useCredit)
                    ? "Con tu saldo"
                    : priced
                      ? `Total ${formatArs(total)}`
                      : "El precio final lo ves en el local"}
                  {time && date ? ` · ${dayParts(date).dow} ${dayParts(date).num}, ${time}` : ""}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={next}
              disabled={!canNext || pending}
              className="inline-flex h-[46px] shrink-0 items-center gap-2 rounded-[var(--bp-r-btn)] bg-[var(--bp-accent)] px-[22px] font-semibold text-[var(--bp-on-accent)] disabled:opacity-40"
              style={btnText}
            >
              {pending && <Loader2 className="size-4 animate-spin" />}
              {ctaText}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function missingFor(
  step: Step,
  c: { hasServices: boolean; hasSlot: boolean; name: boolean; phone: boolean; accept: boolean },
): string | null {
  if (step === "servicio") return c.hasServices ? "Ese servicio no lo hace la persona del link" : "Elegí al menos un servicio";
  if (step === "profesional") return "Elegí con quién";
  if (step === "horario") return "Elegí un día y un horario";
  if (step === "datos") {
    const faltan: string[] = [];
    if (!c.name) faltan.push("tu nombre");
    if (!c.phone) faltan.push("tu celular");
    if (!c.accept) faltan.push("aceptar las condiciones");
    return faltan.length ? `Falta ${faltan.join(", ").replace(/, ([^,]*)$/, " y $1")}` : null;
  }
  return null;
}

function Field({ label, id, help, children }: { label: string; id: string; help?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold">
        {label}
      </label>
      {children}
      {help && <span className="text-xs text-[var(--bp-muted)]">{help}</span>}
    </div>
  );
}

function PersonOption({
  name,
  role,
  initial,
  photoUrl,
  on,
  onPick,
  radius,
  dashed,
}: {
  name: string;
  role: string | null;
  initial: string;
  photoUrl: string | null;
  on: boolean;
  onPick: () => void;
  radius: string;
  dashed?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPick}
      className={`flex items-center gap-3 rounded-[var(--bp-r)] border p-3 text-left ${on ? "border-[var(--bp-accent)] bg-[var(--bp-surface)]" : dashed ? "border-dashed border-[var(--bp-muted)]" : "border-[var(--bp-line)] bg-[var(--bp-surface)]"}`}
    >
      {photoUrl ? (
        <img src={photoUrl} alt="" className="size-11 shrink-0 object-cover" style={{ borderRadius: radius }} />
      ) : (
        <span className={`flex size-11 shrink-0 items-center justify-center text-lg ${dashed ? "border border-dashed border-[var(--bp-muted)] text-[var(--bp-muted)]" : "bg-[var(--bp-tint)]"}`} style={{ borderRadius: radius }}>
          {initial}
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-semibold">{name}</span>
        {role && <span className="truncate text-[13px] text-[var(--bp-muted)]">{role}</span>}
      </span>
      <span
        className="size-4 shrink-0 rounded-full border-2"
        style={{ borderColor: on ? "var(--bp-accent)" : "var(--bp-line)", background: on ? "var(--bp-accent)" : "transparent" }}
        aria-hidden
      />
    </button>
  );
}
